import { evolve, map, append, pluck, reject, isNil, uniq, toPairs, chain, reduce, pipe, filter, not } from 'ramda'
import { isEmpty } from '@aspectus/vue-utils'
import { parametersMiddleware } from '@aspectus/resource'

type Transformer<T> = (s: Record<string, any>) => T

export function keyedTransformer<T>(
  transformer: Transformer<T>,
  from: string,
  to: string = from,
  transformOnUndefined = false,
  dflt: any = undefined
): (source: Record<string, any>, result: Record<string, any>) => Record<string, any> {
  return function fn(source: Record<string, any>, result: Record<string, any>): Record<string, any> {
    const data =
      source && 'undefined' !== typeof source[from] && null !== source[from]
        ? source[from]
        : dflt

    if (!transformOnUndefined && 'undefined' === typeof data) {
      return result
    }

    return {
      ...result,
      [to]: transformer(data),
    }
  }
}

const k = keyedTransformer

export function symmetricalKeyTransformers<F, T>(
  fromHandler: Transformer<F>,
  toHandler: Transformer<T>,
  fromKey: string,
  toKey: string = fromKey
): [(source: Record<string, any>, result: Record<string, any>) => Record<string, any>, (source: Record<string, any>, result: Record<string, any>) => Record<string, any>] {
  return [
    keyedTransformer(fromHandler, fromKey, toKey),
    keyedTransformer(toHandler, toKey, fromKey),
  ]
}

export function sameTransformer<T>(data: T): T {
  return data
}

export function simpleTransformer<T>(data: T): (value: T) => T | undefined {
  return pipe(sameTransformer, checkEmpty)
}

export function toNumber<T>(data: T): number {
  return (data === '' || Number.isNaN(Number(data))) ? null : Number(data)
}

export function composeTransformers(
  ...transformers: any[]
): (source: Record<string, any>) => Record<string, any> {
  const prepared = transformers.reverse()

  return function fn(source: Record<string, any>): Record<string, any> {
    return prepared.reduce((acc, transform) => transform(source, acc), {})
  }
}

export const normalizeSelectValue = (value: any | any[]): any[] =>
  Array.isArray(value) || 'undefined' === typeof value || null === value
    ? value || []
    : [value]

export const checkEmpty = <T>(value: T): T | undefined =>
  isEmpty(value) ? undefined : value

const ar = <T>(value: T | T[]): T | T[] =>
  Array.isArray(value) ? value : [value]

export const selectFrom = ({ id }: Partial<{ id: string | number }>): string | number | undefined => id
export const selectEmptyFrom = (v: Partial<{ id: string | number }>): string | number | null => v?.id || null

export const selectSlugFrom = ({ slug }: Partial<{ slug: string | number }> = {}): string | number | undefined => slug

export const multiTransformFrom = transformer => pipe(ar, map(transformer), filter(pipe(isEmpty, not)))
export const multiselectFrom = pipe(ar, map(selectFrom), filter(pipe(isEmpty, not)))

export const multiselectSlugFrom = pipe(ar, map(selectSlugFrom), filter(pipe(isEmpty, not)))

const selectValueFrom = ({ value }: Partial<{ value: string | number }>): string | number | undefined => value

const multiselectValueFrom = pipe(
  ar,
  map(selectValueFrom),
  filter(pipe(isEmpty, not))
)

const selectIdFrom = ({ id }: Partial<{ id: string | number }>): string | number | undefined => id

const multiselectIdFrom = pipe(
  ar,
  map(selectIdFrom),
  filter(pipe(isEmpty, not))
)

const dateFrom = (date: string): string => new Date(date).toISOString()

const dateOnlyFrom = (date: string): string => {
  const dateTransform = new Date(date)
  return `${dateTransform.getFullYear()}-${
    dateTransform.getMonth() + 1
  }-${dateTransform.getDate()}`
}

const yearFrom = (date: string): number | undefined => {
  const dateTransform = new Date(date)
  return date ? dateTransform.getFullYear() : undefined
}

const dateTo = (date: string): Date | null => (date ? new Date(date) : null)

const dateRangeFrom = evolve({ min: dateFrom, max: dateFrom })

export const dateOnlyRangeFrom = evolve({ min: dateOnlyFrom, max: dateOnlyFrom })

export const rangeFrom = evolve({ min: sameTransformer, max: sameTransformer })

export const yearRangeFrom = evolve({ min: yearFrom, max: yearFrom })

const combinedInputFrom = map(
  composeTransformers(
    k<any>(selectFrom, 'element', 'id'),
    k<any>(sameTransformer, 'search')
  )
)

interface AnyObject {
  [key: string]: any
}

export const simplifiers = {
  inherit: (key: string, toKey: string, transformer): (source: AnyObject, result: AnyObject) => AnyObject => k(transformer, key, toKey),
  multiInherit: (key: string, toKey: string, transformer): (source: AnyObject, result: AnyObject) => AnyObject => k(multiTransformFrom(transformer), key, toKey),
  toNumber: (key: string): (source: AnyObject, result: AnyObject) => AnyObject => k(toNumber, key),
  same: (key: string, toKey?: string): (source: AnyObject, result: AnyObject) => AnyObject => k(sameTransformer, key, toKey, true),
  simple: (key: string, toKey?: string): (source: AnyObject, result: AnyObject) => AnyObject => k(pipe(sameTransformer, checkEmpty), key, toKey),
  search: (key: string): (source: AnyObject, result: AnyObject) => AnyObject => k(pipe(sameTransformer, checkEmpty), key),
  switch: (key: string, toKey?: string): (source: AnyObject, result: AnyObject) => AnyObject => k(pipe(selectFrom, checkEmpty), key, toKey),
  select: (key: string, toKey?: string): (source: AnyObject, result: AnyObject) => AnyObject => k(pipe(selectFrom, checkEmpty), key, toKey),
  selectValue: (key: string): (source: AnyObject, result: AnyObject) => AnyObject => k(pipe(selectValueFrom, checkEmpty), key),
  selectId: (key: string): (source: AnyObject, result: AnyObject) => AnyObject => k(pipe(selectIdFrom, checkEmpty), key),
  selectEmpty: (key: string): (source: AnyObject, result: AnyObject) => AnyObject => k(pipe(selectEmptyFrom), key, key, true),
  selectSimple: (key: string): (source: AnyObject, result: AnyObject) => AnyObject => k(pipe(selectFrom), key),
  multiselect: (key: string, toKey?: string): (source: AnyObject, result: AnyObject) => AnyObject => k(pipe(multiselectFrom, checkEmpty), key, toKey),
  multiselectValue: (key: string): (source: AnyObject, result: AnyObject) => AnyObject => k(pipe(multiselectValueFrom, checkEmpty), key),
  multiselectId: (key: string): (source: AnyObject, result: AnyObject) => AnyObject => k(pipe(multiselectIdFrom, checkEmpty), key),
  multiselectSimple: (key: string, toKey?: string): (source: AnyObject, result: AnyObject) => AnyObject => k(pipe(multiselectFrom), key, toKey),
  range: (key: string): (source: AnyObject, result: AnyObject) => AnyObject => k(pipe(sameTransformer, checkEmpty), key),
  dateTo: (key: string): (source: AnyObject, result: AnyObject) => AnyObject => k(pipe(dateTo), key),
  dateRange: (key: string): (source: AnyObject, result: AnyObject) => AnyObject => k(pipe(dateRangeFrom, checkEmpty), key),
  dateOnlyRangeFrom: (key: string): (source: AnyObject, result: AnyObject) => AnyObject => k(pipe(dateOnlyRangeFrom, checkEmpty), key),
  onlyDate: (key: string): (source: AnyObject, result: AnyObject) => AnyObject => k(pipe(dateOnlyFrom, checkEmpty), key),
  autocompleteMultiselect: (key: string): (source: AnyObject, result: AnyObject) => AnyObject => k(pipe(multiselectFrom, checkEmpty), key),
  combinedInput: (key: string): (source: AnyObject, result: AnyObject) => AnyObject => k(pipe(combinedInputFrom, checkEmpty), key),
}

export const filterAPIMiddleware = (transformer: (params: Record<string, any>) => Record<string, any>): (parameters: Record<string, any>) => Record<string, any> =>
  parametersMiddleware((parameters: Record<string, any>): Record<string, any> => ({
    ...parameters,
    filters: JSON.stringify(transformer(parameters.filters)),
  }))

export const parametersAPIMiddleware = (transformer: (params: Record<string, any>) => Record<string, any>): (parameters: Record<string, any>) => Record<string, any> =>
  parametersMiddleware((parameters: Record<string, any>): Record<string, any> => ({
    ...parameters,
    ...transformer(parameters.filters),
  }))
